import { useCallback, useEffect, useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { DocumentProcessingStatusType, DocumentRecord } from '../../../models/Document'
import { AIAgentType } from '../../../models/AIAgent'

import { useGetAllContactChangesQuery } from '../../../store/meetingSupport/meetingSupport'
import { useGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useGetAllDocumentsQuery, useGetDocumentsQuery } from '../../../store/ai/documents'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import {
  selectDocumentContactFilter,
  selectDocumentCreatedByFilter,
  selectDocumentEndDateFilter,
  selectDocumentStartDateFilter,
  selectDocumentTypeFilter,
} from '../../../store/processDocument/documentFilter'
import { useAppSelector } from '../../../store/hooks'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { useGetRetentionPoliciesQuery } from '../../../store/account/account'
import { RetentionResourceType, RetentionRuleType } from '../../../models/RetentionPolicy'

const _SEARCH_TERM_MIN_LENGTH = 3

export const useDocuments = () => {
  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id

  const contactId = useAppSelector(selectDocumentContactFilter)
  const documentTypeId = useAppSelector(selectDocumentTypeFilter)
  const createdByUserIds = useAppSelector(selectDocumentCreatedByFilter)
  const startDate = useAppSelector(selectDocumentStartDateFilter)
  const endDate = useAppSelector(selectDocumentEndDateFilter)

  const effectiveCreatedByUserIds = createdByUserIds ?? [currentUserId]

  const [activeSearchTerm, setActiveSearchTerm] = useState<string | null>(null)

  const { page, pageSize, searchTerm } = useDocumentSearchParams()

  const [debouncedSearchTerm] = useDebounce(searchTerm, 400)

  useEffect(() => {
    if (!debouncedSearchTerm) {
      setActiveSearchTerm(null)
      return
    }

    if (debouncedSearchTerm.length >= _SEARCH_TERM_MIN_LENGTH) {
      setActiveSearchTerm(debouncedSearchTerm)
    }
  }, [debouncedSearchTerm])

  const { data: retentionPoliciesResponse, isFetching: retentionPoliciesFetching } = useGetRetentionPoliciesQuery({ accountId })

  const accountHasZDRPolicy = useMemo(
    () =>
      !!retentionPoliciesResponse?.data.find(({ resource, rule }) => resource.type === RetentionResourceType.DOCUMENT && rule.type === RetentionRuleType.ZERO),
    [retentionPoliciesResponse],
  )

  const { data: documentsResponse, isFetching: documentsFetching } = useGetDocumentsQuery(
    accountHasZDRPolicy
      ? skipToken
      : {
          accountId,
          pageRequest: { page: page - 1, size: pageSize },
          ...(contactId ? { contactId } : {}),
          ...(documentTypeId ? { documentTypeId } : {}),
          ...(activeSearchTerm ? { searchTerm: activeSearchTerm } : {}),
          ...(effectiveCreatedByUserIds.length ? { createdByUserIds: effectiveCreatedByUserIds.join(',') } : {}),
          ...(startDate && endDate ? { createdAfter: startDate.toISOString(), createdBefore: endDate.toISOString() } : {}),
        },
    { refetchOnMountOrArgChange: true },
  )

  const documents = useMemo(() => documentsResponse?.data ?? [], [documentsResponse?.data])

  const groupedDocuments = useMemo(() => {
    const acc: Record<string, DocumentRecord[]> = {}

    for (const document of documents) {
      const key = document.documentBatchId ?? document.id

      const group = acc[key] ?? []
      group.push(document)

      acc[key] = group
    }

    return acc
  }, [documents])

  return {
    documentsResponse,
    documents,
    groupedDocuments,
    isLoading: documentsFetching || retentionPoliciesFetching,
    accountHasZDRPolicy,
  }
}

export const useDocumentProcessingStatus = (document: DocumentRecord) => {
  const accountId = useAppSelector(selectAccountId)

  const { id: documentId, summaryProcessing } = document

  const summaryProcessingFinished = !!summaryProcessing.finishedAt

  const { data: aiAgentResult, isFetching: aiAgentResultFetching } = useGetAiAgentResultQuery(
    summaryProcessingFinished ? { accountId, type: AIAgentType.DOCUMENT_SUMMARY, resultKey: documentId } : skipToken,
  )

  const { data: contactChangesResponse, isFetching: contactChangesFetching } = useGetAllContactChangesQuery(
    summaryProcessingFinished ? { accountId, filters: { type: 'document', typeKey: documentId } } : skipToken,
  )

  const isLoading = useMemo(() => aiAgentResultFetching || contactChangesFetching, [aiAgentResultFetching, contactChangesFetching])

  const contactChanges = useMemo(() => contactChangesResponse?.data ?? [], [contactChangesResponse])

  const processingStatusType = useMemo(() => {
    if (!summaryProcessingFinished) {
      return DocumentProcessingStatusType.PROCESSING
    }

    if (isLoading) {
      return DocumentProcessingStatusType.LOADING
    }

    const aiAgentResultHasSyncStatus = aiAgentResult?.syncStatus.length
    const contactChangeHasSyncStatus = contactChanges.some((contactChange) => contactChange.syncStatus?.length)

    if (aiAgentResultHasSyncStatus || contactChangeHasSyncStatus) {
      return DocumentProcessingStatusType.SAVED
    }

    return DocumentProcessingStatusType.READY
  }, [aiAgentResult, contactChanges, isLoading, summaryProcessingFinished])

  return { processingStatusType }
}

const _PAGE_PARAM_KEY = 'page'
const _PAGE_SIZE_PARAM_KEY = 'pageSize'
const _SEARCH_TERM_PARAM_KEY = 'term'

export const useDocumentSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const page = useMemo(() => parseInt(searchParams.get(_PAGE_PARAM_KEY) ?? '1'), [searchParams])

  const pageSize = useMemo(() => parseInt(searchParams.get(_PAGE_SIZE_PARAM_KEY) ?? '20'), [searchParams])

  const searchTerm = useMemo(() => searchParams.get(_SEARCH_TERM_PARAM_KEY) ?? '', [searchParams])

  const handlePageChange = useCallback(
    (page: number) => {
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set(_PAGE_PARAM_KEY, String(page))

      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams],
  )

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set(_PAGE_SIZE_PARAM_KEY, String(pageSize))

      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams],
  )

  const handleSearchTermChange = useCallback(
    (searchTerm: string) => {
      const newSearchParams = new URLSearchParams(searchParams)
      if (!searchTerm) {
        newSearchParams.delete(_SEARCH_TERM_PARAM_KEY)
      } else {
        newSearchParams.set(_SEARCH_TERM_PARAM_KEY, searchTerm)
      }

      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams],
  )

  const handleClearSearchTerm = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete(_SEARCH_TERM_PARAM_KEY)
    setSearchParams(newSearchParams)
  }, [searchParams, setSearchParams])

  return { page, pageSize, searchTerm, handlePageChange, handlePageSizeChange, handleSearchTermChange, handleClearSearchTerm }
}

export type DocumentMatchedBy = 'both' | 'name' | 'hash'

interface DocumentMatch {
  document?: DocumentRecord
  matchedBy?: DocumentMatchedBy
}

export const useExistingDocument = (contactId: string, fileName: string, fileHash: string) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: byNameResponse, isFetching: byNameFetching } = useGetAllDocumentsQuery({ accountId, contactId, fileName })

  const { data: byHashResponse, isFetching: byHashFetching } = useGetAllDocumentsQuery({ accountId, contactId, fileHash })

  const documentsByName = useMemo(() => byNameResponse?.data ?? [], [byNameResponse])

  const documentsByHash = useMemo(() => byHashResponse?.data ?? [], [byHashResponse])

  const match = useMemo<DocumentMatch>(() => {
    const documentsByHashIds = new Set(documentsByHash.map((document) => document.id))

    const both = documentsByName.find((document) => documentsByHashIds.has(document.id))
    if (both) {
      return { document: both, matchedBy: 'both' }
    }

    if (documentsByName.length) {
      return { document: documentsByName[0], matchedBy: 'name' }
    }

    if (documentsByHash.length) {
      return { document: documentsByHash[0], matchedBy: 'hash' }
    }

    return {}
  }, [documentsByHash, documentsByName])

  return { document: match.document, matchedBy: match.matchedBy, isLoading: byNameFetching || byHashFetching }
}
